import React, { useEffect, useState } from "react";
import DynamicModuleList from "./components/DynamicModuleList";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import {
  CREATE_DYNAMIC_MODULE,
  FETCH_DYNAMIC_INFO,
  FETCH_DYNAMIC_MODULES,
  SAVE_ENVIRONMENT_DYNAMIC_INFO,
} from "../../apiClient/apiEndpoints";
import { useSelector } from "react-redux";
import apiClient from "../../apiClient";
import { CloseIcon, SearchesCIcon, FileLineIcon, CSVIcon, SpinnerIcon } from "../../Static/icons/iconshere";
import Button from "react-bootstrap/Button";
import csvtojson from "csvtojson";
import moment from "moment/moment";

const MODAL_MODE = {
  HOTSPOT: "hotspot",
  INTERACTIVE_OBJECT: "interactive",
};

const saveFile = async (data, name) => {
  const blob = new Blob([data], { type: "text/csv" });
  const a = document.createElement("a");
  a.download = name;
  a.href = URL.createObjectURL(blob);
  a.addEventListener("click", (e) => {
    setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
  });
  a.click();
};

const ScenarioBuilder = () => {
  const configData = useSelector((state) => state.configSlice);
  const UNO_URL = configData?.config?.UNO_URL;
  const { uuid: environmentId } = useParams();
  const navigate = useNavigate();
  const [moduleName, setModuleName] = useState("");
  const [error, setError] = useState("");
  const [modulesList, setModulesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState("");
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [hotspotData, setHotspotData] = useState([]);
  const [interactiveObjectData, setInteractiveObjectData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [existingHotspotData, setExistingHotspotData] = useState({});
  const [csvModalError, setCsvModalError] = useState("");
  const showLastUpdated = {
    [MODAL_MODE.HOTSPOT]: existingHotspotData?.data?.hotspots?.length > 0,
    [MODAL_MODE.INTERACTIVE_OBJECT]: existingHotspotData?.data?.interactableObjects?.length > 0,
  };

  let filteredList = modulesList;
  if (search) {
    filteredList = modulesList.filter((item) => item?.name?.toLowerCase().includes(search?.toLowerCase()));
  }

  const fetchDynamicModuleList = async () => {
    const res = await apiClient.get(`${UNO_URL}${FETCH_DYNAMIC_MODULES}/${environmentId}`);
    setModulesList(res);
  };

  const onSearchChange = (e) => setSearch(e.target.value);
  const closeCreateModuleModal = () => {
    setError("");
    setModuleName("");
    setShowCreateModal(false);
  };
  const showCreateModuleModal = () => setShowCreateModal(true);

  const createDynamicModule = async () => {
    try {
      if (!moduleName?.trim()) {
        setError("Module name can not be empty");
        return;
      } else {
        setError("");
      }
      const payload = {
        moduleName: moduleName,
      };
      const res = await apiClient.post(`${UNO_URL}${CREATE_DYNAMIC_MODULE}/${environmentId}`, payload);
      closeCreateModuleModal();
      navigate(`/dashboard/environments/scenario/${environmentId}/edit/${res.uuid}`);
    } catch (error) {
      console.log(error);
    }
  };

  const onModuleClick = (module) => {
    navigate(`/dashboard/environments/scenario/${environmentId}/edit/${module.uuid}`);
  };

  const closeCsvModal = () => {
    setShowCsvModal(false);
    setModalMode("");
    setHotspotData([]);
    setInteractiveObjectData([]);
    setCsvLoading(false);
    setCsvModalError("");
  };

  const openCsvModal = (value) => {
    setModalMode(value);
    setShowCsvModal(true);
  };

  const loadHotSpotAndObjectData = async () => {
    const res = await apiClient.get(`${UNO_URL}${FETCH_DYNAMIC_INFO}/${environmentId}`);
    setExistingHotspotData(res?.versions?.[0]);
  };

  const initializePage = async () => {
    setLoading(true);
    await Promise.all([fetchDynamicModuleList(), loadHotSpotAndObjectData()]);
    setLoading(false);
  };

  const saveCsvHandler = async () => {
    try {
      if (csvModalError) return;
      const payload = {};
      if (modalMode === MODAL_MODE.HOTSPOT && hotspotData?.length > 0) {
        setCsvModalError("");
        payload.hotspots = hotspotData;
      } else if (modalMode === MODAL_MODE.HOTSPOT) {
        setCsvModalError("Please upload a file first");
        return;
      } else if (modalMode === MODAL_MODE.INTERACTIVE_OBJECT && interactiveObjectData?.length > 0) {
        setCsvModalError("");
        payload.interactableObjects = interactiveObjectData;
      } else if (modalMode === MODAL_MODE.INTERACTIVE_OBJECT) {
        setCsvModalError("Please upload a file first");
        return;
      }
      setCsvLoading(true);
      const res = await apiClient.post(`${UNO_URL}${SAVE_ENVIRONMENT_DYNAMIC_INFO}/${environmentId}`, payload);
      closeCsvModal();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      loadHotSpotAndObjectData();
    }
  };

  function validateInfoObject(info) {
    const requiredProperties = ["Id", "Value"];

    for (const prop of requiredProperties) {
      if (!(prop in info) || (typeof info[prop] === "string" && !info[prop].trim()) || !info[prop]) {
        return false;
      }
    }
    return true;
  }

  const isFileValid = (list) => {
    for (const info of list) {
      if (!validateInfoObject(info)) {
        return false;
      }
    }
    return true;
  };

  const downloadCsv = async () => {
    const csvHeaders = ["Id", "Value"];
    if (modalMode === MODAL_MODE.HOTSPOT) {
      const csvValues = existingHotspotData.data.hotspots.map((item) => `${item.id}, ${item.value}`);
      const csvContent = `${csvHeaders.join(",")}\n${csvValues.join("\n")}`;
      await saveFile(csvContent, "Hotspot.csv");
    }
    if (modalMode === MODAL_MODE.INTERACTIVE_OBJECT) {
      const csvValues = existingHotspotData.data.interactableObjects.map((item) => `${item.id}, ${item.value}`);
      const csvContent = `${csvHeaders.join(",")}\n${csvValues.join("\n")}`;
      await saveFile(csvContent, "InteractiveObjects.csv");
    }
  };

  const onFileUpload = async (e) => {
    e.preventDefault();
    const file = e.target.files?.[0] || e.dataTransfer.files[0];
    const reader = new FileReader();
    if (file && file.type === "text/csv") {
      reader.onload = async (e) => {
        const csvString = e.target.result;
        const jsonArray = await csvtojson().fromString(csvString);
        if (jsonArray.length) {
          if (isFileValid(jsonArray)) {
            setCsvModalError("");
            const fileData = jsonArray.map((item) => ({ id: String(item.Id).trim(), value: item.Value.trim() }));
            if (modalMode === MODAL_MODE.HOTSPOT) {
              setHotspotData(fileData);
            }
            if (modalMode === MODAL_MODE.INTERACTIVE_OBJECT) {
              setInteractiveObjectData(fileData);
            }
          } else {
            setCsvModalError("Invalid file format.");
          }
        } else {
          setCsvModalError("File is empty.");
        }
      };
    } else {
      setCsvModalError("Only CSV file is allowed");
    }
    reader.readAsText(file);
  };

  useEffect(() => {
    initializePage();
  }, []);

  return loading ? (
    <> Data is fetching... </>
  ) : (
    <>
      <div className="stuckhere modules scenario">
        <div className="row">
          <div className="col-md-6">
            <form className=" app-search app-search1 d-flex justify-content-start ">
              <div className="position-relative">
                <SearchesCIcon />
                <input
                  placeholder="Search Modules...."
                  className="form-control"
                  type="text"
                  value={search}
                  onChange={onSearchChange}
                />
              </div>
            </form>
          </div>
          <div className="col-md-6 d-flex align-item-center justify-content-end">
            <button className="createbtn" onClick={() => openCsvModal(MODAL_MODE.HOTSPOT)}>
              Hotspots CSV
            </button>
            <button className="createbtn" onClick={() => openCsvModal(MODAL_MODE.INTERACTIVE_OBJECT)}>
              Active Objects CSV
            </button>
            <button className="createbtn" onClick={showCreateModuleModal}>
              Add Modules
            </button>
          </div>
        </div>
      </div>
      <div className="lowersection scenario">
        <div className="container-fluid px-4">
          <div className="modulelist pt-0">
            <DynamicModuleList list={filteredList} onModuleClick={onModuleClick} />
          </div>
        </div>
      </div>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} className="NewModule" centered size="md">
        <Modal.Header className="modalheader">
          Enter Module Details
          <span onClick={() => setShowCreateModal(false)}>
            <CloseIcon />
          </span>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 mb-2">
                <div className="form-group">
                  <label className="label">Name</label>
                  <input
                    autoComplete="off"
                    placeholder="Module Name"
                    name="moduleName"
                    id="moduleName"
                    className="input form-control"
                    type="text"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                  />
                </div>
                {error && <p className="error"> {error} </p>}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="footerbtn pt-0">
          <Button onClick={closeCreateModuleModal}> Cancel </Button>
          <Button onClick={createDynamicModule}> Save </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCsvModal} centered onHide={closeCsvModal}>
        <Modal.Header className="modalheader">
          Upload {modalMode === "hotspot" ? "Hotspots" : "Interactive Objects"} CSV
        </Modal.Header>
        <Modal.Body>
          <div className="upload-csv">
            <label
              htmlFor="fileUpload"
              className={`upload-box ${
                !csvModalError && (hotspotData?.length > 0 || interactiveObjectData?.length > 0) && "border-success"
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onFileUpload}
            >
              <input type="file" accept=".csv" id="fileUpload" onChange={onFileUpload} className="hidden" />
              <label htmlFor="fileUpload" className="cursor-pointer">
                <CSVIcon className="text-5xl text-green-500 mx-auto" />
                <p className="mt-2 text-gray-600">
                  Drag & Drop or <span className="text-blue-500">Upload CSV File</span>
                </p>
              </label>
            </label>
            {!csvModalError && (hotspotData?.length > 0 || interactiveObjectData?.length > 0) && (
              <p className="mt-2 text-sm text-success">Upload Complete</p>
            )}
            <p className="error">{csvModalError}</p>

            {/* <div className="upload-box">
            <input type="file" accept=".csv" onChange={onFileUpload} style={{ height: "300px", zIndex: 10000 }} />
            <CSVIcon />
              <p>
                {" "}
                Drag Drop or <a> Upload CSV File </a>
              </p>
            </div> */}
            <div className="uploaded-file-parent">
              <h6>Latest Upload</h6>
              <div className="uploaded-file">
                <span className="file-name">
                  <span className="file-icon">
                    <FileLineIcon />
                  </span>
                  <p>
                    {showLastUpdated[modalMode]
                      ? `Last updated at ${moment(existingHotspotData.updatedAt).format("DD MMM YYYY HH:mm")}`
                      : "No Data Found"}
                  </p>
                </span>
                {showLastUpdated[modalMode] && (
                  <span className="action-items">
                    <span className="download" onClick={downloadCsv}>
                      Download
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="footerbtn pt-0">
          <button onClick={closeCsvModal}> Cancel </button>

          {csvLoading ? (
            <Button>
              {" "}
              <SpinnerIcon />
            </Button>
          ) : (
            <button onClick={saveCsvHandler} disabled={csvModalError}>
              {" "}
              Save{" "}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ScenarioBuilder;
